<template>
    <div class="messenger-button" :class="[
        { 'mobile-hidden': !showOnMobile && isMobile },
        `position-${position}`
    ]">
        <!-- Speech Bubble -->
        <div
            v-if="showSpeechBubble"
            class="speech-bubble"
            :class="{ 'typing': isTyping }"
            ref="speechBubble"
            @click="handleClick"
        >
            <button
                type="button"
                class="speech-bubble-close"
                @click.stop="hideSpeechBubble"
                aria-label="Close speech bubble"
            >
                <i class="fas fa-times"></i>
            </button>
            <div class="speech-bubble-content">
                {{ displayedText }}
                <span v-if="isTyping" class="typing-cursor">|</span>
            </div>
            <div class="speech-bubble-arrow"></div>
        </div>

        <button
            type="button"
            class="floating-button"
            :class="[
                { [`floating-button-${buttonColor}`]: !customBackgroundColor },
                `position-${position}`
            ]"
            @click="handleClick"
            :title="buttonText"
            :aria-label="buttonText"
            ref="floatingButton"
            :style="buttonStyle"
        >
            <!-- Custom image or icon -->
            <img
                v-if="buttonImageUrl"
                :src="buttonImageUrl"
                :alt="buttonImageAlt"
                class="floating-image"
                @error="handleImageError"
            />
            <i
                v-else
                :class="buttonIcon"
                class="floating-icon"
                :style="{ color: customTextColor || 'inherit' }"
            ></i>
        </button>
    </div>
</template>

<script>
export default {
    name: 'MessengerButton',

    props: {
        buttonIcon: {
            type: String,
            default: 'fas fa-question-circle'
        },
        buttonImageUrl: {
            type: String,
            default: null // Custom image URL for the button
        },
        buttonImageAlt: {
            type: String,
            default: 'Chat Button'
        },
        buttonColor: {
            type: String,
            default: 'primary',
            validator: value => ['primary', 'secondary', 'success', 'info', 'warning', 'danger'].includes(value)
        },
        buttonText: {
            type: String,
            default: 'Ask a question'
        },
        position: {
            type: String,
            default: 'bottom-right',
            validator: value => ['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(value)
        },
        showOnMobile: {
            type: Boolean,
            default: true
        },
        speechBubbleText: {
            type: String,
            default: 'If you have any questions, you can ask me'
        },
        showSpeechBubbleDelay: {
            type: Number,
            default: 1000 // Show after 1 second for testing
        },
        speechBubbleTypingSpeed: {
            type: Number,
            default: 50 // Milliseconds per character
        },
        isModalOpen: {
            type: Boolean,
            default: false
        },
        customBackgroundColor: {
            type: String,
            default: null // Custom hex color (overrides buttonColor)
        },
        customTextColor: {
            type: String,
            default: null // Custom text color for the button
        }
    },

    data() {
        return {
            isMobile: false,
            showSpeechBubble: false,
            isTyping: false,
            displayedText: '',
            speechBubbleTimeout: null,
            typingInterval: null
        }
    },

    computed: {
        buttonStyle() {
            const style = {
                // Only custom colors here, positioning handled by CSS classes
            }

            // Apply custom colors if provided
            if (this.customBackgroundColor) {
                style.backgroundColor = this.customBackgroundColor
                style.borderColor = this.customBackgroundColor
            }
            if (this.customTextColor) {
                style.color = this.customTextColor
            }

            return style
        }
    },

    mounted() {
        this.checkMobile()
        window.addEventListener('resize', this.checkMobile)
        window.addEventListener('resize', this.updateSpeechBubblePosition)
        this.scheduleSpeechBubble()
        this.$nextTick(() => {
            this.updateSpeechBubblePosition()
        })
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile)
        window.removeEventListener('resize', this.updateSpeechBubblePosition)
        this.clearTimeouts()
    },

    methods: {
        checkMobile() {
            this.isMobile = window.innerWidth <= 768
        },

        scheduleSpeechBubble() {
            this.speechBubbleTimeout = setTimeout(() => {
                this.showSpeechBubbleWithTyping()
            }, this.showSpeechBubbleDelay)
        },

        showSpeechBubbleWithTyping() {
            this.showSpeechBubble = true
            this.isTyping = true
            this.displayedText = ''

            // Position the speech bubble relative to button
            this.$nextTick(() => {
                this.updateSpeechBubblePosition()
            })

            let currentIndex = 0
            const text = this.speechBubbleText

            this.typingInterval = setInterval(() => {
                if (currentIndex < text.length) {
                    this.displayedText += text[currentIndex]
                    currentIndex++
                } else {
                    this.isTyping = false
                    clearInterval(this.typingInterval)
                    // Keep the speech bubble visible for styling - comment out auto-hide
                    // setTimeout(() => {
                    //     this.hideSpeechBubble()
                    // }, 3000)
                }
            }, this.speechBubbleTypingSpeed)
        },

        updateSpeechBubblePosition() {
            if (!this.$refs.speechBubble || !this.$refs.floatingButton) return

            const button = this.$refs.floatingButton
            const buttonRect = button.getBoundingClientRect()
            const buttonBottom = window.innerHeight - buttonRect.bottom

            // Position speech bubble 20px above the button
            const speechBubbleBottom = buttonBottom + buttonRect.height + 20

            this.$refs.speechBubble.style.bottom = speechBubbleBottom + 'px'

            // Position horizontally based on button position
            if (this.position.includes('left')) {
                // For left positions, align speech bubble to left edge of button
                this.$refs.speechBubble.style.left = buttonRect.left + 'px'
                this.$refs.speechBubble.style.right = 'auto'
            } else {
                // For right positions, align speech bubble to right edge of button
                this.$refs.speechBubble.style.right = (window.innerWidth - buttonRect.right) + 'px'
                this.$refs.speechBubble.style.left = 'auto'
            }
        },

        hideSpeechBubble() {
            this.showSpeechBubble = false
            this.displayedText = ''
            this.isTyping = false
        },

        handleClick() {
            // Hide speech bubble when opening modal
            if (!this.isModalOpen) {
                this.hideSpeechBubble()
                this.clearTimeouts()
            }
            // Emit click event with button position for modal positioning
            this.$emit('click', { position: this.position })
        },

        clearTimeouts() {
            if (this.speechBubbleTimeout) {
                clearTimeout(this.speechBubbleTimeout)
            }
            if (this.typingInterval) {
                clearInterval(this.typingInterval)
            }
        },

        handleImageError(event) {
            console.warn('Failed to load button image:', this.buttonImageUrl)
            // Hide the broken image and show fallback icon
            event.target.style.display = 'none'
            // You could emit an event here to notify parent component
            this.$emit('image-load-error', this.buttonImageUrl)
        }
    }
}
</script>

<style>
.messenger-button {
    z-index: 1060; /* Above the fixed-bottom enter buttons */
    transition: all 0.3s ease;
    /* Container for positioning - button itself will be positioned */
}

/* Speech Bubble - will be positioned dynamically via component */
.speech-bubble {
    position: fixed;
    right: 20px;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    padding: 12px 16px;
    max-width: 200px;
    animation: bubbleAppear 0.3s ease-out;
    transform-origin: bottom right;
    z-index: 1061; /* Above the button */
    cursor: pointer; /* Make it clear it's clickable */
    transition: transform 0.2s ease; /* Add smooth hover effect */
}

.speech-bubble:hover {
    transform: scale(1.02); /* Slight scale on hover */
}

.speech-bubble-close {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #6c757d;
    border: 2px solid white;
    color: white;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1062;
    transition: all 0.2s ease;
}

.speech-bubble-close:hover {
    background: #495057;
    transform: scale(1.1);
}

.speech-bubble-content {
    font-size: 14px;
    color: #495057;
    line-height: 1.4;
    white-space: normal;
    word-wrap: break-word;
}

.speech-bubble-arrow {
    position: absolute;
    bottom: -10px;
    right: 13px;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #ffffff00;
}

.speech-bubble-arrow::before {
    content: '';
    position: absolute;
    bottom: 1px;
    left: 50%;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 9px solid #ffffff;
}

.typing-cursor {
    animation: blink 1s infinite;
    font-weight: bold;
    color: #007bff;
}

/* Position adjustments for different button positions */
.position-bottom-left .speech-bubble-arrow {
    left: 20px;
    right: auto;
}

.position-top-left .speech-bubble-arrow {
    left: 20px;
    right: auto;
}

.position-top-right .speech-bubble,
.position-top-left .speech-bubble {
    top: 80px;
    bottom: auto;
}

.position-top-right .speech-bubble-arrow,
.position-top-left .speech-bubble-arrow {
    top: -8px;
    bottom: auto;
    border-top: none;
    border-bottom: 8px solid white;
}

.position-top-right .speech-bubble-arrow::before,
.position-top-left .speech-bubble-arrow::before {
    top: 1px;
    bottom: auto;
    border-top: none;
    border-bottom: 9px solid #e9ecef;
}

/* Animations */
@keyframes bubbleAppear {
    from {
        opacity: 0;
        transform: scale(0.8) translateY(10px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

/* Mobile adjustments */
@media (max-width: 768px) {
    .speech-bubble {
        max-width: 180px;
        font-size: 13px;
        padding: 10px 14px;
    }
}

/* Floating button animations and effects */

.floating-icon {
    font-size: 36px !important;
    font-weight: bold !important;
    line-height: 1 !important;
}

.floating-button {
    position: fixed;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: none !important;
    outline: none !important;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1060;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease;
    cursor: pointer;
    background: #007bff;
}

.floating-button:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.25);
}

.floating-button:active {
    transform: scale(0.95);
}

/* Bootstrap color variants */
.floating-button-primary { background: #007bff; }
.floating-button-secondary { background: #6c757d; }
.floating-button-success { background: #28a745; }
.floating-button-info { background: #17a2b8; }
.floating-button-warning { background: #ffc107; color: #212529; }
.floating-button-danger { background: #dc3545; }

.floating-icon {
    font-size: 24px;
}

.floating-image {
    width: 60px !important;
    height: 60px !important;
    max-width: 60px;
    max-height: 60px;
    min-width: 60px;
    min-height: 60px;
    border-radius: 50%;
    object-fit: cover;
    object-position: center center;
    display: block;
    margin: 0;
    background: transparent;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Position variants */
.floating-button.position-bottom-right {
    bottom: 20px;
    right: 20px;
}

.floating-button.position-bottom-left {
    bottom: 20px;
    left: 20px;
}

.floating-button.position-top-right {
    top: 20px;
    right: 20px;
}

.floating-button.position-top-left {
    top: 20px;
    left: 20px;
}


/* Mobile adjustments */
@media (max-width: 768px) {
    .floating-button {
        width: 55px;
        height: 55px;
    }

    .floating-icon {
        font-size: 20px;
    }

    .floating-image {
        width: 55px !important;
        height: 55px !important;
        max-width: 55px;
        max-height: 55px;
        min-width: 55px;
        min-height: 55px;
        object-position: center center;
    }

    .position-bottom-right {
        bottom: 15px;
        right: 15px;
    }

    .position-bottom-left {
        bottom: 15px;
        left: 15px;
    }

    .position-top-right {
        top: 15px;
        right: 15px;
    }

    .position-top-left {
        top: 15px;
        left: 15px;
    }

    .mobile-hidden {
        display: none;
    }
}

/* Accessibility */
.floating-button:focus {
    outline: none !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06), 0 0 0 3px rgba(0, 123, 255, 0.25);
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
    .floating-button, .messenger-button {
        animation: none;
        transition: none;
    }

    .floating-button:hover {
        transform: none;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Messenger/MessengerButton.vue